import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { checkPermission, createLogger, type Permission } from '@metron/shared';

const logger = createLogger('ProtectedRoute');

const REDIRECT_AFTER_LOGIN_KEY = 'wellspring:redirect_after_login';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Permission required to access this route. Uses the shared Permission enum. */
  requiredPermission?: Permission;
}

export function ProtectedRoute({ children, requiredPermission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we're processing an auth callback
    const isCallbackPage = window.location.pathname === '/callback';
    const hasAuthCode = new URLSearchParams(window.location.search).get('code');

    // Don't trigger redirect if we're on the callback page or processing auth
    if (isCallbackPage || hasAuthCode) {
      return;
    }

    // Redirect to login page if not authenticated
    if (!isLoading && !isAuthenticated) {
      // Save the intended destination so we can return after login
      const intendedUrl = location.pathname + location.search;
      if (intendedUrl !== '/') {
        sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, intendedUrl);
      }
      logger.info('User not authenticated, redirecting to login page...');
      navigate('/login');
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  useEffect(() => {
    // After authentication completes, redirect to the saved URL if one exists.
    // This handles the case where Cognito redirects back to root (/) after OAuth,
    // and we need to send the user to the page they originally requested.
    if (!isLoading && isAuthenticated) {
      const savedUrl = sessionStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
      if (savedUrl) {
        sessionStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
        navigate(savedUrl, { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show loading while redirect happens
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  // Check permission if required
  if (requiredPermission) {
    const userGroups = user?.groups ?? [];
    if (!checkPermission(userGroups, requiredPermission)) {
      return (
        <div className='flex flex-col items-center justify-center min-h-screen'>
          <h1 className='text-2xl font-bold mb-4'>Access Denied</h1>
          <p className='text-muted-foreground'>
            You don't have permission to access this page.
          </p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
